import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { SectionTag } from "@/components/site/Reveal";
import { serviceIcons } from "@/i18n/content";
import { SERVICE_IMAGES } from "@/i18n/assets";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, dir } = useLang();
  const idx = parseInt(slug, 10);
  const service = t.services.items[idx];
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!service) {
    return (
      <SiteLayout>
        <Seo title="404" />
        <div className="min-h-[55vh] grid place-items-center px-5 text-center" data-testid="service-not-found">
          <div>
            <h1 className="text-5xl font-extrabold mb-3">404</h1>
            <p className="text-slate-400 mb-6">{t.serviceDetail.back}</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>
              {t.legal.backHome}
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }
  const Icon = serviceIcons[idx];

  const goContact = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 300);
  };

  return (
    <SiteLayout>
      <Seo title={service.t} description={service.d} />
      <article className="pb-20" dir="auto" data-testid="service-detail">
        <div className="relative h-[42vh] min-h-[300px] overflow-hidden">
          <img src={SERVICE_IMAGES[idx]} alt={service.t} className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.55), var(--bg))" }} />
          <div className="absolute bottom-0 inset-x-0">
            <div className="mx-auto max-w-4xl px-5 sm:px-8 pb-8">
              <div className="h-14 w-14 rounded-2xl grid place-items-center mb-4" style={{ background: "rgba(0,242,96,0.15)", backdropFilter: "blur(6px)" }}>
                <Icon size={26} className="text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{service.t}</h1>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-5 sm:px-8 mt-10">
          <SectionTag>{t.serviceDetail.overview}</SectionTag>
          <p className="text-xl text-slate-300 leading-relaxed mt-5 mb-10">{service.d}</p>

          <h2 className="text-2xl font-extrabold mb-5">{t.serviceDetail.features}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {t.why.items.slice(0, 6).map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="card-surface rounded-2xl p-5 flex items-center gap-3">
                <CheckCircle2 size={22} className="text-primary shrink-0" />
                <span className="font-medium">{f}</span>
              </motion.div>
            ))}
          </div>

          <div className="card-surface rounded-3xl p-8 text-center">
            <h3 className="text-2xl font-extrabold mb-3">{service.t}</h3>
            <p className="text-slate-400 mb-6">{t.contact.subtitle}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={goContact} className="rounded-full px-7 py-3.5 text-sm font-bold text-[#04120a]" style={{ background: "var(--primary)" }} data-testid="service-cta">{t.serviceDetail.cta}</button>
              <Link to="/" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold border border-white/15 hover:bg-white/5 transition-colors">
                <Arrow size={16} /> {t.serviceDetail.back}
              </Link>
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}
